/**
 * Beep Sound Utility
 * 
 * Generates a beep sound programmatically using Web Audio API
 * Used for QR scanner confirmation feedback
 */

/**
 * Play a beep sound
 * @param {number} duration - Duration in milliseconds (default: 200ms)
 * @param {number} frequency - Frequency in Hz (default: 800Hz)
 * @param {number} volume - Volume from 0 to 1 (default: 0.3)
 */
export const playBeep = (duration = 200, frequency = 800, volume = 0.3) => {
    return new Promise((resolve, reject) => {
        try {
            // Check if AudioContext is available
            const AudioContext = window.AudioContext || window.webkitAudioContext
            if (!AudioContext) {
                console.warn('Web Audio API not supported')
                resolve()
                return
            }

            const audioContext = new AudioContext()
            const oscillator = audioContext.createOscillator()
            const gainNode = audioContext.createGain()

            oscillator.connect(gainNode)
            gainNode.connect(audioContext.destination)

            oscillator.frequency.value = frequency
            oscillator.type = 'sine'

            gainNode.gain.setValueAtTime(volume, audioContext.currentTime)
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000)

            oscillator.start(audioContext.currentTime)
            oscillator.stop(audioContext.currentTime + duration / 1000)

            oscillator.onended = () => {
                audioContext.close()
                resolve()
            }
        } catch (error) {
            console.error('Error playing beep sound:', error)
            reject(error)
        }
    })
}

/**
 * Play a success beep (two-tone)
 */
export const playSuccessBeep = async () => {
    try {
        await playBeep(150, 800, 0.3)
        await new Promise(resolve => setTimeout(resolve, 50))
        await playBeep(150, 1000, 0.3)
    } catch (error) {
        console.error('Error playing success beep:', error)
    }
}

/**
 * Play an error beep (lower frequency)
 */
export const playErrorBeep = async () => {
    try {
        await playBeep(300, 400, 0.3)
    } catch (error) {
        console.error('Error playing error beep:', error)
    }
}
